(function () {
  'use strict';

  /* ======================================================================
     EDITABLE PRODUCT MESSAGING CONFIGURATION
     Update wording and production times here. Rules below reference these
     values and do not repeat business-facing text.
     ====================================================================== */
  const PRODUCT_MESSAGE_CONFIG = {
    debug: false,

    messages: {
      defaultHeights: `Available in 6'8", 7'0" & 8'0"`,
      heights68And70: `Available in 6'8" & 7'0"`,
      heights68And80: `Available in 6'8" & 8'0"`,
      height68Only: `Available in 6'8"`,
      height80Only: `Available in 8'0"`,
      sale70: `7'0" on SALE`,
      customBuilt: 'Custom Built to Order',
      customDisclaimer: 'THIS IS A CUSTOM BUILT TO ORDER DOOR AND IS NOT RETURNABLE. PREHUNG PRODUCTS ARE SHIPPED KNOCKED-DOWN (DISASSEMBLED).',
      jambIncluded: 'Jamb & Hardware Included',
      trackIncluded: 'Track & Hardware Included',
      optionalAddons: 'Displayed with Optional Addons',
      slabsReady: 'Ready to ship',
      readyToShip: 'Ready to ship',
      fireRated: '90m Fire Rated',
      productionWeeks: (weeks) => `Up to ${weeks} ${weeks === 1 ? 'Week' : 'Weeks'} Production`,
      preparationOverlay: (weeks) => `Up to ${weeks} ${weeks === 1 ? 'Week' : 'Weeks'} Production`,
      inventoryReady: 'Ready to ship or pick-up.',
      inventoryWeeks: (weeks) => `Up to ${weeks} ${weeks === 1 ? 'week' : 'weeks'} to prepare.`,
      inventoryPreparation: (weeks) =>
        `Ready to ship or pick-up. *Up to ${weeks} ${weeks === 1 ? 'week' : 'weeks'} lead time with preparation options.`
    },

    leadTimes: {
      louverInsertWeeks: 1,
      preparationWeeks: 1,
      cncWeeks: 3,
      exteriorWeeks: 3,
      groovedWeeks: 5,
      seriesWeeks: 7,
      prehungDoubleQuickShipWeeks: 1
    },

    cardBanners: {
      // Easy on/off switch. Keep false to suppress the generic preparation
      // message on cards while retaining it for product/inventory messaging.
      showDefaultPreparation: false
    },

    productTypes: {
      doors: ['interior door', 'exterior door', 'commercial door'],
      excluded: [
        'residential hardware',
        'commercial hardware',
        'hardware',
        'hinges',
        'trim',
        'moulding',
        'molding'
      ]
    },

    doorTags: [
      'slab',
      'prehung',
      'double',
      'bypass',
      'triple',
      'triple bypass',
      'barn',
      'pocket',
      'bifold'
    ],

    customBuiltTags: ['d-series', 'f-series', 'grooved', 'exterior door'],
    groovedTitleFallbacks: [
      'aria',
      'brio',
      'diamond',
      'fava',
      'forte',
      'legato',
      'moresco',
      'sonata',
      'stanza'
    ]
  };

  const normalize = (value) => String(value || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  const normalizeList = (values) => (Array.isArray(values) ? values : []).map(normalize).filter(Boolean);
  const includesAny = (text, terms) => terms.some((term) => text.includes(term));
  const hasTag = (product, tag) => product.tags.includes(tag);
  const endsWithPhrase = (value, phrase) => normalize(value).replace(/[-_]+/g, ' ').endsWith(phrase);

  function classifyProduct(rawProduct) {
    const product = {
      id: rawProduct && rawProduct.id,
      handle: normalize(rawProduct && rawProduct.handle),
      title: normalize(rawProduct && rawProduct.title),
      productType: normalize(rawProduct && rawProduct.productType),
      tags: normalizeList(rawProduct && rawProduct.tags),
      url: (rawProduct && rawProduct.url) || '',
      collectionHandles: normalizeList(rawProduct && rawProduct.collectionHandles),
      variants: Array.isArray(rawProduct && rawProduct.variants) ? rawProduct.variants : []
    };

    const excludedText = [product.productType, product.title, ...product.tags].join(' ');
    const isExcludedProduct = includesAny(excludedText, PRODUCT_MESSAGE_CONFIG.productTypes.excluded);
    const typeIsDoor = PRODUCT_MESSAGE_CONFIG.productTypes.doors.includes(product.productType);
    const hasDoorTag = product.tags.some((tag) => PRODUCT_MESSAGE_CONFIG.doorTags.includes(tag));
    const titleDoorFallback = /\b(door|slab|prehung|bifold|bypass|pocket)\b/.test(product.title);
    const endsInDouble = endsWithPhrase(product.title, 'double') || endsWithPhrase(product.handle, 'double');
    const endsInDoubleWithLouverInsert =
      endsWithPhrase(product.title, 'double with louver insert') ||
      endsWithPhrase(product.handle, 'double with louver insert') ||
      endsWithPhrase(product.title, 'double with lover insert') ||
      endsWithPhrase(product.handle, 'double with lover insert');
    const isDoor = !isExcludedProduct && (typeIsDoor || hasDoorTag || titleDoorFallback || endsInDouble || endsInDoubleWithLouverInsert);

    return Object.assign(product, {
      isExcludedProduct,
      isDoor,
      isSlab: hasTag(product, 'slab') || /\bslab\b/.test(product.title),
      isPrehung: hasTag(product, 'prehung') || /\bprehung\b/.test(product.title),
      // A Double tag still helps classify a door, but production/jamb rules
      // require the title or handle itself to end in "double". This prevents
      // names such as "Flush Double Barn" from being treated as prehung Double.
      isDouble: endsInDouble,
      isDoubleWithLouverInsert: endsInDoubleWithLouverInsert,
      hasDoubleTag: hasTag(product, 'double'),
      isBifold: hasTag(product, 'bifold') || /\bbifold\b/.test(product.title),
      isExteriorDoor: product.productType === 'exterior door' || hasTag(product, 'exterior door'),
      isFireRated: hasTag(product, 'fire rated') || /\bfire rated\b/.test(product.title),
      isDSeries: hasTag(product, 'd-series') || product.title.includes('d-series'),
      isFSeries: hasTag(product, 'f-series') || product.title.includes('f-series') || product.title.includes('carved'),
      isGrooved:
        hasTag(product, 'grooved') ||
        PRODUCT_MESSAGE_CONFIG.groovedTitleFallbacks.some((name) => product.title.includes(name)),
      hasLouverInsert:
        endsWithPhrase(product.title, 'louver insert') ||
        endsWithPhrase(product.handle, 'louver insert') ||
        endsWithPhrase(product.title, 'lover insert') ||
        endsWithPhrase(product.handle, 'lover insert'),
      isFullLouver: product.title.includes('full louver')
    });
  }

  function isCustomBuilt(rawProduct) {
    const product = rawProduct && Object.prototype.hasOwnProperty.call(rawProduct, 'isDoor')
      ? rawProduct
      : classifyProduct(rawProduct);
    return Boolean(
      product.isDoor &&
      (product.isPrehung ||
        product.isDouble ||
        product.isDSeries ||
        product.isFSeries ||
        product.isGrooved ||
        product.isExteriorDoor ||
        product.hasLouverInsert)
    );
  }

  function getLeadTime(rawProduct) {
    const product = rawProduct && Object.prototype.hasOwnProperty.call(rawProduct, 'isDoor')
      ? rawProduct
      : classifyProduct(rawProduct);
    const times = PRODUCT_MESSAGE_CONFIG.leadTimes;
    const messages = PRODUCT_MESSAGE_CONFIG.messages;

    if (!product.isDoor) {
      return { id: 'not-door', weeks: null, overlay: '', inventory: '' };
    }

    // Exterior doors use their own production lead time, regardless of
    // whether they are otherwise classified as slabs or prehung products.
    if (product.isExteriorDoor) {
      return {
        id: 'exterior-production',
        weeks: times.exteriorWeeks,
        overlay: messages.productionWeeks(times.exteriorWeeks),
        inventory: messages.inventoryWeeks(times.exteriorWeeks)
      };
    }

    if (product.isDSeries || product.isFSeries) {
      return { id: 'series-production', weeks: times.seriesWeeks, overlay: messages.productionWeeks(times.seriesWeeks), inventory: messages.inventoryWeeks(times.seriesWeeks) };
    }
    if (product.isGrooved) {
      return { id: 'grooved-production', weeks: times.groovedWeeks, overlay: messages.productionWeeks(times.groovedWeeks), inventory: messages.inventoryWeeks(times.groovedWeeks) };
    }
    if (product.hasLouverInsert) {
      return { id: 'louver-insert-production', weeks: times.louverInsertWeeks, overlay: messages.productionWeeks(times.louverInsertWeeks), inventory: messages.inventoryWeeks(times.louverInsertWeeks) };
    }
    // Prehung and ending-Double configurations override the generic Full
    // Louver/slab ready-to-ship rule.
    if (product.isPrehung || product.isDouble) {
      return {
        id: 'prehung-double-production',
        weeks: times.prehungDoubleQuickShipWeeks,
        overlay: messages.productionWeeks(times.prehungDoubleQuickShipWeeks),
        inventory: messages.inventoryWeeks(times.prehungDoubleQuickShipWeeks)
      };
    }
    // Ready-to-ship applies only to non-custom Slabs and Bifolds. Full Louver
    // alone is not enough; it must still be classified as a Slab or Bifold.
    if (!isCustomBuilt(product) && (product.isSlab || product.isBifold)) {
      return {
        id: product.isSlab ? 'slab-ready' : 'bifold-ready',
        weeks: 0,
        overlay: messages.readyToShip,
        inventory: messages.inventoryReady
      };
    }
    return {
      id: 'door-preparation',
      weeks: times.preparationWeeks,
      overlay: messages.preparationOverlay(times.preparationWeeks),
      inventory: messages.inventoryPreparation(times.preparationWeeks)
    };
  }

  const OVERLAY_RULES = [
    {
      id: 'included-jamb', message: () => PRODUCT_MESSAGE_CONFIG.messages.jambIncluded,
      match: (p) => p.isDoor && (p.isPrehung || p.isDouble || p.isDoubleWithLouverInsert), exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'bottom-right', style: 'included', priority: 1000, group: 'includedHardware'
    },
    {
      id: 'included-track', message: () => PRODUCT_MESSAGE_CONFIG.messages.trackIncluded,
      match: (p) => p.isDoor && p.isBifold, exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'bottom-right', style: 'production', priority: 990, group: 'includedHardware'
    },
    {
      id: 'optional-addons', message: () => PRODUCT_MESSAGE_CONFIG.messages.optionalAddons,
      match: (p) => p.isDoor && p.productType === 'commercial door' && !p.isSlab,
      exclusions: (p) => p.isExcludedProduct, contexts: ['card', 'product'], position: 'bottom-right', style: 'included', priority: 980, group: 'includedHardware'
    },
    {
      id: 'custom-built', message: () => PRODUCT_MESSAGE_CONFIG.messages.customBuilt,
      match: (p) => isCustomBuilt(p), exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'bottom-right', style: 'production', priority: 950, group: 'customStatus'
    },
    {
      id: 'fire-rated', message: () => PRODUCT_MESSAGE_CONFIG.messages.fireRated,
      match: (p) => p.isFireRated, exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'bottom-right', style: 'sale', priority: 940, group: 'fireRated'
    },
    {
      id: 'production-status', message: (p) => getLeadTime(p).overlay,
      match: (p, context) => {
        if (!p.isDoor) return false;
        if (context !== 'card') return true;
        return PRODUCT_MESSAGE_CONFIG.cardBanners.showDefaultPreparation || getLeadTime(p).id !== 'door-preparation';
      },
      exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'top-left', style: 'production', priority: 750, group: 'productionStatus'
    },
    {
      id: 'height-bifold-commercial-wood', message: () => PRODUCT_MESSAGE_CONFIG.messages.heights68And70,
      match: (p) => p.isDoor && ((p.isBifold && !includesAny(p.title, ['smooth', 'shaker', 'birch'])) || includesAny(p.title, ['commercial birch', 'commercial flush birch', 'commercial oak', 'commercial flush oak'])),
      exclusions: (p) => p.isExcludedProduct, contexts: ['card', 'product'], position: 'top-left', style: 'height', priority: 850, group: 'heightAvailability'
    },
    {
      id: 'height-caiman-oak', message: () => PRODUCT_MESSAGE_CONFIG.messages.heights68And80,
      match: (p) => p.isDoor && (p.title.includes('caiman') || (p.title.includes('oak') && !p.title.includes('red oak'))),
      exclusions: (p) => p.isExcludedProduct, contexts: ['card', 'product'], position: 'top-left', style: 'height', priority: 840, group: 'heightAvailability'
    },
    {
      id: 'height-8-only', message: () => PRODUCT_MESSAGE_CONFIG.messages.height80Only,
      match: (p) => p.isDoor && includesAny(p.title, ['6 lite', '18 lite']), exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'top-left', style: 'height', priority: 830, group: 'heightAvailability'
    },
    {
      id: 'height-steel-arch', message: () => PRODUCT_MESSAGE_CONFIG.messages.heights68And70,
      match: (p) => p.isDoor && p.title.includes('2 panel arch steel'), exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'top-left', style: 'height', priority: 820, group: 'heightAvailability'
    },
    {
      id: 'height-68-only', message: () => PRODUCT_MESSAGE_CONFIG.messages.height68Only,
      match: (p) => p.isDoor && (includesAny(p.title, ['rockport', 'red oak (6', 'birch bifold', '5 lite', 'french']) || (p.title.includes('smooth') && !p.title.includes('flush')) || (p.title.includes('flat shaker') && includesAny(p.title, ['3 panel', '5 panel', 'bifold']))),
      exclusions: (p) => p.isExcludedProduct, contexts: ['card', 'product'], position: 'top-left', style: 'height', priority: 810, group: 'heightAvailability'
    },
    {
      id: 'height-default-door', message: () => PRODUCT_MESSAGE_CONFIG.messages.defaultHeights,
      match: (p) => p.isDoor, exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'top-left', style: 'height', priority: 800, group: 'heightAvailability'
    },
    {
      id: 'sale-caiman-70', message: () => PRODUCT_MESSAGE_CONFIG.messages.sale70,
      match: (p) => p.isDoor && p.title.includes('caiman'), exclusions: (p) => p.isExcludedProduct,
      contexts: ['card', 'product'], position: 'top-left', style: 'sale', priority: 700, group: 'sale'
    }
  ];

  function selectRules(product, context) {
    const usedGroups = new Set();
    const commercialHeightsOnly = product.productType === 'commercial door';
    return OVERLAY_RULES.slice().sort((a, b) => b.priority - a.priority).filter((rule) => {
      // Commercial doors intentionally show height availability only. Suppress
      // included-hardware, optional-addons, custom, production, ready and sale
      // banners in both card and product-page contexts.
      if (commercialHeightsOnly && rule.group !== 'heightAvailability') return false;
      if (!rule.contexts.includes(context) || rule.exclusions(product) || !rule.match(product, context)) return false;
      if (rule.group && usedGroups.has(rule.group)) return false;
      if (rule.group) usedGroups.add(rule.group);
      return true;
    });
  }

  function parseProductData(container) {
    const dataElement = container.querySelector(':scope > .custom-product-messaging-data, .custom-product-messaging-data');
    if (!dataElement) return null;
    try {
      return JSON.parse(dataElement.textContent);
    } catch (error) {
      if (PRODUCT_MESSAGE_CONFIG.debug) console.warn('[Product messaging] Invalid product JSON', error);
      return null;
    }
  }

  function renderOverlays(host, product, context) {
    if (!host) return;
    // Remove containers left by the retired custom-jamb/custom-rule scripts.
    // This prevents a cached or duplicate legacy render from showing two sets.
    host.querySelectorAll(':scope > .product-label-container.js-dynamic').forEach((node) => node.remove());
    host.querySelectorAll(':scope > [data-custom-product-messaging="overlay"]').forEach((node) => node.remove());
    host.classList.add('custom-product-overlay-host');

    const positions = new Map();
    selectRules(product, context).forEach((rule) => {
      let position = positions.get(rule.position);
      if (!position) {
        position = document.createElement('div');
        position.className = `custom-product-overlay custom-product-overlay--${rule.position}`;
        position.dataset.customProductMessaging = 'overlay';
        host.appendChild(position);
        positions.set(rule.position, position);
      }
      const label = document.createElement('span');
      label.className = `custom-product-overlay__label custom-product-overlay__label--${rule.style}`;
      label.dataset.ruleId = rule.id;
      label.textContent = rule.message(product);
      position.appendChild(label);
    });
  }

  function findProductDescription(section) {
    const scopedDescription = section.querySelector('.rte.product-description');
    if (scopedDescription) return scopedDescription;

    // Some product templates render the description outside the immediate
    // .product.js-product wrapper but inside the same Shopify section.
    const shopifySection = section.closest('[id^="shopify-section-"]');
    const sectionDescription = shopifySection && shopifySection.querySelector('.rte.product-description');
    if (sectionDescription) return sectionDescription;

    // Safe product-page fallback: use a document-level match only when there
    // is exactly one description container.
    const pageDescriptions = document.querySelectorAll('.rte.product-description');
    return pageDescriptions.length === 1 ? pageDescriptions[0] : null;
  }

  function appendCustomDisclaimer(section, product) {
    if (!isCustomBuilt(product)) return false;
    const description = findProductDescription(section);
    if (!description || description.querySelector('.custom-built-disclaimer')) return Boolean(description);
    const expected = normalize(PRODUCT_MESSAGE_CONFIG.messages.customDisclaimer);
    if (normalize(description.textContent).includes(expected)) return true;

    const paragraph = document.createElement('p');
    paragraph.className = 'custom-built-disclaimer';
    const color = document.createElement('span');
    color.style.color = 'rgb(255, 42, 0)';
    const strong = document.createElement('strong');
    strong.textContent = PRODUCT_MESSAGE_CONFIG.messages.customDisclaimer;
    color.appendChild(strong);
    paragraph.appendChild(color);
    description.appendChild(paragraph);
    return true;
  }

  function retryCustomDisclaimer(section, product) {
    if (!isCustomBuilt(product) || section.dataset.customDisclaimerRetryScheduled === 'true') return;
    section.dataset.customDisclaimerRetryScheduled = 'true';
    [250, 1000, 2500].forEach((delay) => {
      window.setTimeout(() => appendCustomDisclaimer(section, product), delay);
    });
  }

  function renderProductOverlays(section, product) {
    section.querySelectorAll('#product-media [data-custom-product-messaging="overlay"]').forEach((node) => node.remove());
    const mainMedia = section.querySelector('#product-media .media-viewer__item.is-current-variant .media');
    renderOverlays(mainMedia, product, 'product');
  }

  function eventVariantId(event) {
    const detail = (event && event.detail) || {};
    const candidate = detail.variant || detail.productVariant || detail.currentVariant || detail;
    const id = Number(candidate && candidate.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function selectedVariantId(section) {
    if (section.__customProductSelectedVariantId) return section.__customProductSelectedVariantId;

    const picker = section.querySelector('variant-picker');
    const pickerVariant = picker && (picker.currentVariant || picker.variant || picker.selectedVariant);
    const pickerId = Number(pickerVariant && pickerVariant.id);
    if (Number.isFinite(pickerId) && pickerId > 0) return pickerId;

    const mainForm = section.querySelector('.js-product-form-main');
    const formId = Number(mainForm && mainForm.querySelector('[name="id"]') && mainForm.querySelector('[name="id"]').value);
    if (Number.isFinite(formId) && formId > 0) return formId;

    const fallback = section.querySelector('select[name="id"][id^="variants-"]');
    const fallbackId = Number(fallback && fallback.value);
    if (Number.isFinite(fallbackId) && fallbackId > 0) return fallbackId;

    const urlId = Number(new URL(window.location.href).searchParams.get('variant'));
    return Number.isFinite(urlId) && urlId > 0 ? urlId : null;
  }

  function updateVariantMessaging(section, product) {
    const priceOutput = section.querySelector('#variant-price');
    const inventoryOutput = section.querySelector('#variant-inventory');
    if (!priceOutput && !inventoryOutput) return;

    const currentPrice = section.querySelector('.product-info__price .price__current');
    if (priceOutput && currentPrice) priceOutput.textContent = `${currentPrice.textContent.trim()} Each`;
    if (!inventoryOutput) return;

    const id = selectedVariantId(section);
    if (!id) {
      inventoryOutput.textContent = 'Select options to see availability';
      inventoryOutput.style.color = 'gray';
      return;
    }

    const variant = product.variants.find((item) => Number(item.id) === id);
    if (!variant) {
      inventoryOutput.textContent = 'Variant unavailable';
      inventoryOutput.style.color = 'gray';
      return;
    }

    if (!variant.inventoryManagement || variant.quantity === null) {
      inventoryOutput.textContent = 'Inventory not tracked';
      inventoryOutput.style.color = 'gray';
      return;
    }

    if (Number(variant.quantity) > 0) {
      const quantity = Number(variant.quantity);
      const units = quantity === 1 ? 'Unit' : 'Units';
      const inventoryLeadTime = getLeadTime(product).inventory;
      inventoryOutput.textContent = `In stock (${quantity} ${units})${inventoryLeadTime ? ` — ${inventoryLeadTime}` : ''}`;
      inventoryOutput.style.color = 'green';
      return;
    }

    inventoryOutput.textContent = 'Unavailable';
    inventoryOutput.style.color = 'red';
  }

  function initializeVariantMessaging(section, product) {
    if (section.dataset.customVariantMessagingBound === 'true') {
      updateVariantMessaging(section, product);
      return;
    }
    section.dataset.customVariantMessagingBound = 'true';

    let timer;
    const queueUpdate = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        renderProductOverlays(section, product);
        updateVariantMessaging(section, product);
      }, 75);
    };
    const handleVariantEvent = (event) => {
      const id = eventVariantId(event);
      if (id) section.__customProductSelectedVariantId = id;
      queueUpdate();
    };

    ['on:variant:change', 'variant:change', 'variant-change', 'product:variant-change'].forEach((eventName) => {
      section.addEventListener(eventName, handleVariantEvent);
    });
    section.addEventListener('change', (event) => {
      if (event.target.matches('variant-picker input, variant-picker select, select[name="id"][id^="variants-"], .js-product-form-main [name="id"], #itoris_dynamicproductoptions input, #itoris_dynamicproductoptions select')) {
        section.__customProductSelectedVariantId = null;
        queueUpdate();
      }
    });

    const itoris = section.querySelector('#itoris_dynamicproductoptions');
    if (itoris) itoris.addEventListener('click', queueUpdate);
    window.setTimeout(() => updateVariantMessaging(section, product), 100);
  }

  function initializeCard(card) {
    const rawProduct = parseProductData(card);
    if (!rawProduct) return;
    const product = classifyProduct(rawProduct);
    renderOverlays(card.querySelector('.card__media'), product, 'card');
  }

  function initializeProductSection(section) {
    const rawProduct = parseProductData(section);
    if (!rawProduct) return;
    const product = classifyProduct(rawProduct);
    renderProductOverlays(section, product);
    if (!appendCustomDisclaimer(section, product)) retryCustomDisclaimer(section, product);
    initializeVariantMessaging(section, product);
  }

  function initializeProductMessaging(rootElement) {
    const root = rootElement || document;
    if (root.nodeType !== 1 && root.nodeType !== 9) return;

    if (root.matches && root.matches('product-card.card--product')) initializeCard(root);
    root.querySelectorAll('product-card.card--product').forEach(initializeCard);

    if (root.matches && root.matches('.product.js-product[data-section]')) initializeProductSection(root);
    root.querySelectorAll('.product.js-product[data-section]').forEach(initializeProductSection);
  }

  function observeDynamicResults() {
    if (!document.body || !window.MutationObserver) return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (
            node.matches('#filter-results, #filter-results .js-pagination-result, product-card.card--product, .product.js-product[data-section]') ||
            node.querySelector('#filter-results, product-card.card--product, .product.js-product[data-section]')
          ) {
            initializeProductMessaging(node);
          }

          if (
            node.matches('.rte.product-description') ||
            node.querySelector('.rte.product-description')
          ) {
            document.querySelectorAll('.product.js-product[data-section]').forEach((section) => {
              const rawProduct = parseProductData(section);
              if (rawProduct) appendCustomDisclaimer(section, classifyProduct(rawProduct));
            });
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.ProductMessaging = {
    config: PRODUCT_MESSAGE_CONFIG,
    rules: OVERLAY_RULES,
    classifyProduct,
    isCustomBuilt,
    getLeadTime,
    initializeProductMessaging
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeProductMessaging(document);
      observeDynamicResults();
    });
  } else {
    initializeProductMessaging(document);
    observeDynamicResults();
  }
})();
